import React, { useState, useRef, useEffect } from 'react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (!MAPBOX_TOKEN) return;
      setLoading(true);
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${MAPBOX_TOKEN}&country=us&types=address&limit=5`
        );
        const data = await res.json();
        const results = (data.features || []).map(f => ({
          id: f.id,
          full: f.place_name,
          address: f.text + (f.address ? ` ${f.address}` : ''),
          city: f.context?.find(c => c.id.startsWith('place'))?.text || '',
          state: f.context?.find(c => c.id.startsWith('region'))?.short_code?.replace('US-', '') || '',
          zip: f.context?.find(c => c.id.startsWith('postcode'))?.text || '',
          lat: f.center[1],
          lng: f.center[0],
        }));
        setSuggestions(results);
        setShowDropdown(results.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (suggestion) => {
    onChange(suggestion.address || suggestion.full);
    setShowDropdown(false);
    setSuggestions([]);
    if (onSelect) onSelect(suggestion);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder || 'Start typing an address...'}
        className={className}
        autoComplete="off"
      />

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-elevated border border-theme rounded-xl shadow-2xl z-50 overflow-hidden">
          {loading && (
            <div className="px-4 py-2.5 text-[12px] text-muted-c">Searching...</div>
          )}
          {suggestions.map(s => (
            <button
              key={s.id}
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-2.5 hover:bg-hover-surface transition-all border-b border-theme last:border-0"
            >
              <div className="flex items-start gap-2.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 text-muted-c flex-shrink-0">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-primary-c truncate">{s.full}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
